/*
 * Copyright (C) 2026 Nex4Nova (Geoblast group)
 * SPDX-License-Identifier: Proprietary
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import {
  Baselode3DScene,
  Baselode3DControls,
  parseDrillholesCSV,
  parseSurveyCSV,
  desurveyTraces,
  classifyColumns,
  getCategoryHexColor,
} from 'baselode';
import 'baselode/style.css';
import './Drillhole.css';
import { useProjectData } from '../../contexts/ProjectDataContext.jsx';
import { useLanguage } from '../../contexts/LanguageContext.jsx';
import { makeLocalProjector } from '../../lib/localProjection.js';
import { buildCategoricalColorMap, colorForCategory } from '../../lib/categoricalColors.js';
import {
  enforceZUpOrbit,
  getSceneViewState,
  setSceneViewState,
  recenterCameraToOriginZUp,
  focusOnLastBoundsZUp,
  lookDownZUp,
  reconcileSceneBounds,
  loadCachedCameraView,
  saveCachedCameraView,
  disposeObject3D,
} from './sceneUtils.js';
import {
  buildAssayIntervalsByHole,
  mapIntervalsForVariable,
  buildEqualRangeColorScale,
  normalizeHoleKey,
  finiteValueRange,
  buildCategoryIntervalsByHole,
  commodityColorForVariable,
  ASSAY_COLOR_PALETTE_10,
} from './intervalUtils.js';
import {
  fitUtmToLocalTransform,
  latLngToUtm,
  toLocalFrameHole,
  applyGeoreferenceIfInRange,
  prepareObjMeshGroup,
} from './geoUtils.js';

const FOV_STEPS = [1, 4, 8, 14, 21, 28];
const SCENE_BG_KEY = 'nex4nova-drilling-3d-dark-bg-v1';

// Scene backgrounds. The default is the light "off-white" canvas that
// geological software (Surfer, Maptek, QGIS 3D) uses by default for
// readability in daylight / print; dark is offered for presentations.
const SCENE_BG_OPTIONS = [
  { key: 'off-white', label: 'Off-white', color: '#F2F1ED' },
  { key: 'white', label: 'White', color: '#FFFFFF' },
  { key: 'gray', label: 'Light gray', color: '#C9CDD3' },
  { key: 'dark-gray', label: 'Dark gray', color: '#2A2E33' },
  { key: 'black', label: 'Black', color: '#000000' },
];
const SCENE_BG_DEFAULT = 'off-white';

function readInitialBgKey() {
  try {
    const v = localStorage.getItem(SCENE_BG_KEY);
    if (v === 'true') return 'black'; // legacy dark
    if (v === 'false') return 'off-white'; // legacy light
    if (SCENE_BG_OPTIONS.some((opt) => opt.key === v)) return v;
  } catch (e) { /* ignore */ }
  return SCENE_BG_DEFAULT;
}

function Drillhole() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const renderedHolesRef = useRef(null);
  const hasFramedContentRef = useRef(false);
  const objFileInputRef = useRef(null);
  const objMeshGroupsRef = useRef(new Map());
  const restoredCameraRef = useRef(false);
  const zoomSliderPrevRef = useRef(50);
  const autoSeedRef = useRef(false);

  const { t } = useLanguage();
  const { collars, assayState, structureRows, geologyHoles, rawCsv, status } = useProjectData();

  // Scene contains only holes the user has explicitly added via the sidebar.
  const [holes, setHoles] = useState([]);
  const [selectedHoleId, setSelectedHoleId] = useState('');
  const [selectedCollarHoleId, setSelectedCollarHoleId] = useState('');
  const [selectedNoSurveyHoleId, setSelectedNoSurveyHoleId] = useState('');
  const [addError, setAddError] = useState('');
  const [objMeshes, setObjMeshes] = useState([]);
  const [objError, setObjError] = useState('');

  const [selectedHole, setSelectedHole] = useState(null);
  const [controlMode, setControlMode] = useState('orbit');
  const [error, setError] = useState('');
  const [colorByVariable, setColorByVariable] = useState('None');
  const [showStructuralDiscs, setShowStructuralDiscs] = useState(true);
  const [showStripLogs, setShowStripLogs] = useState(false);
  // Default to ~1/4 along the slider (closer to Ortho than full Perspective).
  const [perspectiveLevel, setPerspectiveLevel] = useState(1);
  const [sceneBgKey, setSceneBgKey] = useState(readInitialBgKey);
  const sceneBackground = SCENE_BG_OPTIONS.find((opt) => opt.key === sceneBgKey)?.color || SCENE_BG_OPTIONS[0].color;
  const darkBackground = sceneBgKey === 'dark-gray' || sceneBgKey === 'black';

  useEffect(() => {
    try { localStorage.setItem(SCENE_BG_KEY, sceneBgKey); } catch (e) { /* ignore */ }
  }, [sceneBgKey]);

  const assayVariables = useMemo(() => {
    const numeric = (assayState?.numericProps || []).filter(Boolean);
    return ['__HAS_ASSAY__', ...numeric];
  }, [assayState]);

  const geologyCategories = useMemo(() => {
    if (!geologyHoles?.length) return [];
    return classifyColumns(geologyHoles.flatMap((h) => h.points || [])).categoricalCols;
  }, [geologyHoles]);

  const isCategorical = useMemo(
    () => geologyCategories.includes(colorByVariable),
    [geologyCategories, colorByVariable]
  );
  const categoricalColorMap = useMemo(
    () => buildCategoricalColorMap(geologyHoles, rawCsv?.colors, colorByVariable),
    [geologyHoles, rawCsv?.colors, colorByVariable],
  );

  const geologyCategoryIntervalsByHole = useMemo(() => {
    if (!isCategorical || !colorByVariable) return null;
    return buildCategoryIntervalsByHole(geologyHoles, colorByVariable);
  }, [isCategorical, geologyHoles, colorByVariable]);

  const assayIntervalsByHole = useMemo(
    () => buildAssayIntervalsByHole(assayState?.holes || []),
    [assayState]
  );

  const selectedAssayIntervalsByHole = useMemo(() => {
    if (colorByVariable === 'None' || isCategorical) return null;
    if (colorByVariable === '__HAS_ASSAY__') return assayIntervalsByHole;
    return mapIntervalsForVariable(assayIntervalsByHole, colorByVariable);
  }, [assayIntervalsByHole, colorByVariable, isCategorical]);

  const stripLogData = useMemo(() => {
    if (!showStripLogs || !holes?.length || !selectedAssayIntervalsByHole || isCategorical
      || colorByVariable === 'None' || colorByVariable === '__HAS_ASSAY__') return null;
    const allValues = Object.values(selectedAssayIntervalsByHole)
      .flatMap((ivs) => ivs.map((iv) => Number(iv.value)))
      .filter(Number.isFinite);
    const valueRange = finiteValueRange(allValues);
    const valueMin = valueRange?.min ?? null;
    const valueMax = valueRange?.max ?? null;
    const color = commodityColorForVariable(colorByVariable);
    const logs = holes.flatMap((hole) => {
      const key = normalizeHoleKey(hole.id);
      const intervals = selectedAssayIntervalsByHole[key];
      if (!intervals?.length) return [];
      const depths = intervals.map((iv) => (iv.from + iv.to) / 2);
      const values = intervals.map((iv) => Number(iv.value));
      if (values.filter(Number.isFinite).length < 2) return [];
      return [{ holeId: hole.id, depths, values, options: { color, valueMin, valueMax } }];
    });
    return logs.length ? logs : null;
  }, [showStripLogs, holes, selectedAssayIntervalsByHole, isCategorical, colorByVariable]);

  const legendScale = useMemo(() => {
    if (!selectedAssayIntervalsByHole || isCategorical || colorByVariable === '__HAS_ASSAY__') return null;
    const values = Object.values(selectedAssayIntervalsByHole)
      .flatMap((intervals) => (intervals || []).map((interval) => Number(interval?.value)))
      .filter(Number.isFinite);
    const scale = buildEqualRangeColorScale(values, ASSAY_COLOR_PALETTE_10);
    return scale?.bins?.length ? scale : null;
  }, [selectedAssayIntervalsByHole, colorByVariable, isCategorical]);

  // Parse survey rows once per project and keep them in memory; individual
  // hole adds re-use this and only desurvey the picked hole. baselode's
  // parseSurveyCSV is async, so we land it in state via useEffect.
  const [surveyRows, setSurveyRows] = useState(null);
  useEffect(() => {
    if (!rawCsv?.survey) {
      setSurveyRows(null);
      return undefined;
    }
    let cancelled = false;
    parseSurveyCSV(rawCsv.survey)
      .then((rows) => { if (!cancelled) setSurveyRows(rows?.length ? rows : null); })
      .catch((e) => {
        console.warn('Survey parse failed:', e);
        if (!cancelled) setSurveyRows(null);
      });
    return () => { cancelled = true; };
  }, [rawCsv?.survey]);

  // Precomputed desurveyed holes indexed by id (when the project supplies
  // a `precomputed_desurveyed` file). Adds prefer this over re-running the
  // desurvey when an entry exists. parseDrillholesCSV is also async.
  const [precomputedByHole, setPrecomputedByHole] = useState({});
  useEffect(() => {
    if (!rawCsv?.precomputed) {
      setPrecomputedByHole({});
      return undefined;
    }
    let cancelled = false;
    parseDrillholesCSV(rawCsv.precomputed)
      .then((parsed) => {
        if (cancelled) return;
        const out = {};
        (parsed?.holes || []).forEach((hole) => {
          const pts = (hole.points || []).map((p) => ({
            x: Number(p.x), y: Number(p.y), z: Number(p.z), md: Number(p.md),
          })).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z));
          if (pts.length) {
            out[hole.id] = { id: hole.id, project: hole.points?.[0]?.project_id || '', points: pts };
          }
        });
        setPrecomputedByHole(out);
      })
      .catch((e) => {
        console.info('Precomputed desurvey parse failed:', e?.message);
        if (!cancelled) setPrecomputedByHole({});
      });
    return () => { cancelled = true; };
  }, [rawCsv?.precomputed]);

  // Fixed local-meters frame, centred on the collar centroid. We compute
  // this from ALL collars (not just the added ones) so the origin stays
  // stable as the user adds/removes holes — the scene doesn't shift under
  // the camera.
  const project = useMemo(() => {
    if (!collars.length) return null;
    const refLat = collars.reduce((s, c) => s + c.lat, 0) / collars.length;
    const refLng = collars.reduce((s, c) => s + c.lng, 0) / collars.length;
    return makeLocalProjector(refLat, refLng);
  }, [collars]);

  // Transform that georeferences loaded OBJ meshes (which are exported in the
  // project's projected CRS, e.g. UTM/MGA easting-northing) into the scene's
  // local-meters frame so they overlay the drillholes. Fitted directly from
  // the collars, which carry both lat/lng and easting/northing — no CRS/zone
  // metadata or proj dependency needed. Null until enough control points exist.
  const utmToLocal = useMemo(() => fitUtmToLocalTransform(collars, project), [collars, project]);

  // Classify every collar by the 3D geometry it can actually produce, so the
  // picker separates holes that render a real drill string from those that
  // don't. Merely appearing in the survey rows isn't enough — a hole with a
  // single survey station desurveys to one point (a collar, not a string), so
  // we bucket on the resulting point count, not on presence:
  //   • full     — precomputed OR desurvey yields >= 2 points → a drill trace
  //   • collar   — no trace, but a precomputed point or a collar elevation
  //                provides one accurate point (rendered at its true Z)
  //   • noSurvey — no trace and no elevation → can't be placed in Z at all
  const holeBuckets = useMemo(() => {
    // Desurvey every collar once (single pass) and record the point count.
    const desurveyCount = new Map();
    if (surveyRows?.length && collars.length) {
      let traces = [];
      try { traces = desurveyTraces(collars, surveyRows) || []; } catch (e) { traces = []; }
      for (const t of traces) {
        if (!t?.id) continue;
        const n = (t.points || []).filter((p) => Number.isFinite(p.z)).length;
        desurveyCount.set(t.id, Math.max(desurveyCount.get(t.id) || 0, n));
      }
    }
    const full = [];
    const collar = [];
    const noSurvey = [];
    const seen = new Set();
    for (const c of collars) {
      const id = c.holeId;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const preN = precomputedByHole[id]?.points?.length || 0;
      const geomN = Math.max(preN, desurveyCount.get(id) || 0);
      if (geomN >= 2) full.push(id);
      else if (preN >= 1 || Number.isFinite(c.elevation)) collar.push(id);
      else noSurvey.push(id);
    }
    full.sort(); collar.sort(); noSurvey.sort();
    return { full, collar, noSurvey };
  }, [collars, surveyRows, precomputedByHole]);

  // Reset scene-holes whenever the project changes underneath us. Without
  // this, leftover holes from a previous project would refuse to clear.
  // Loaded OBJ meshes go too: they were georeferenced against the previous
  // project's collars, so keeping them would leave stale geometry in the
  // scene and in the bounds.
  useEffect(() => {
    setHoles([]);
    setAddError('');
    setError('');
    setSelectedHoleId('');
    setSelectedCollarHoleId('');
    setSelectedNoSurveyHoleId('');
    autoSeedRef.current = false;
    if (objMeshGroupsRef.current.size) {
      objMeshGroupsRef.current.forEach((group) => {
        sceneRef.current?.scene?.remove(group);
        disposeObject3D(group);
      });
      objMeshGroupsRef.current.clear();
    }
    setObjMeshes([]);
    setObjError('');
  }, [collars, rawCsv?.survey, rawCsv?.precomputed]);

  // De-dupe happens inside the functional updater so the latest state is
  // consulted, not whatever `holes` was in the closure when this callback
  // was created. That removes a TOCTOU window if Add is fired twice before
  // React commits the first append. As a bonus we can drop `holes` from
  // the deps so the callback stays referentially stable.
  const addHoleToScene = useCallback((holeId) => {
    if (!holeId) return;
    setAddError('');

    const appendIfNew = (entry) => {
      setHoles((prev) => (prev.some((h) => h.id === entry.id) ? prev : [...prev, entry]));
    };

    // 1) Precomputed wins. Precomputed desurvey points are stored in the
    // project's projected CRS (UTM/MGA easting-northing), whereas the survey
    // path below emits local meters via `project`. Reproject them into the
    // same local frame so the whole scene — holes and loaded OBJ meshes —
    // shares one coordinate system. Falls back to the source frame when there
    // are no collar control points to fit the transform from.
    const pre = precomputedByHole[holeId];
    if (pre) {
      appendIfNew(toLocalFrameHole(pre, utmToLocal));
      return;
    }

    // 2) Survey-based desurvey for just this one collar.
    if (!surveyRows) {
      setAddError(`No survey data available for ${holeId}.`);
      return;
    }
    if (!project) {
      setAddError('Local projection not ready (no collars).');
      return;
    }
    const collar = collars.find((c) => c.holeId === holeId);
    if (!collar) {
      setAddError(`Collar not found for ${holeId}.`);
      return;
    }
    let desurveyed;
    try {
      desurveyed = desurveyTraces([collar], surveyRows);
    } catch (e) {
      setAddError(e?.message || `Desurvey failed for ${holeId}.`);
      return;
    }
    if (!desurveyed?.length) {
      setAddError(`No survey rows match ${holeId}.`);
      return;
    }
    const h = desurveyed[0];
    // baselode emits survey-trace Z relative to the collar (z=0 at the
    // collar). Lift it to the collar RL when known so survey-derived traces
    // share the same absolute vertical datum as collar-only holes.
    const collarElevation = Number(collar.elevation);
    const zOffset = Number.isFinite(collarElevation) ? collarElevation : 0;
    const pts = (h.points || [])
      .map((p) => {
        const xy = utmToLocal?.projectLatLng(p.lat ?? 0, p.lng ?? 0) || project(p.lat ?? 0, p.lng ?? 0);
        if (!Number.isFinite(xy.x) || !Number.isFinite(xy.y) || !Number.isFinite(p.z)) return null;
        return { x: xy.x, y: xy.y, z: zOffset + p.z, md: p.md };
      })
      .filter(Boolean);
    if (!pts.length) {
      setAddError(`No projectable points for ${holeId}.`);
      return;
    }
    appendIfNew({ id: h.id, project: h.project, points: pts });
  }, [collars, surveyRows, precomputedByHole, project, utmToLocal]);

  // Auto-seed the scene: the first time a project is ready, add every
  // desurveyable hole and pick an assay colour-by so the view opens
  // populated instead of empty. Manual adds/removes are untouched; the
  // flag resets with the project (see the reset effect) so a new project
  // re-seeds.
  useEffect(() => {
    if (autoSeedRef.current || status !== 'ready' || !holeBuckets.full.length) return;
    autoSeedRef.current = true;
    if (colorByVariable === 'None' && assayVariables.length > 1) {
      setColorByVariable(assayVariables[1]);
    }
    holeBuckets.full.forEach((holeId) => addHoleToScene(holeId));
  }, [status, holeBuckets, assayVariables, colorByVariable, addHoleToScene]);

  // Add a collar-only hole: no desurveyed string, but the collar carries an
  // elevation, so we can place a single accurate point at (collar x/y, RL).
  // The scene renders a lone point as a collar circle. Kept separate from
  // addHoleToScene because there is no trace to desurvey — the collar RL is
  // the only Z we have (baselode's desurvey starts traces at z=0 and ignores
  // collar elevation, so a real RL never reaches the string path).
  const addCollarPointToScene = useCallback((holeId) => {
    if (!holeId) return;
    setAddError('');
    if (!project) {
      setAddError('Local projection not ready (no collars).');
      return;
    }
    const collar = collars.find((c) => c.holeId === holeId);
    if (!collar) {
      setAddError(`Collar not found for ${holeId}.`);
      return;
    }
    const z = Number(collar.elevation);
    if (!Number.isFinite(z)) {
      setAddError(`No collar elevation for ${holeId}.`);
      return;
    }
    const xy = utmToLocal?.projectLatLng(collar.lat, collar.lng) || project(collar.lat, collar.lng);
    if (!Number.isFinite(xy?.x) || !Number.isFinite(xy?.y)) {
      setAddError(`Could not project collar for ${holeId}.`);
      return;
    }
    const entry = { id: holeId, project: collar.project, points: [{ x: xy.x, y: xy.y, z, md: 0 }] };
    setHoles((prev) => (prev.some((h) => h.id === entry.id) ? prev : [...prev, entry]));
  }, [collars, project, utmToLocal]);

  const removeHoleFromScene = useCallback((holeId) => {
    setHoles((prev) => prev.filter((h) => h.id !== holeId));
  }, []);

  const removeObjMeshFromScene = useCallback((meshId) => {
    const group = objMeshGroupsRef.current.get(meshId);
    if (group && sceneRef.current?.scene) {
      sceneRef.current.scene.remove(group);
      disposeObject3D(group);
    }
    objMeshGroupsRef.current.delete(meshId);
    setObjMeshes((prev) => prev.filter((mesh) => mesh.id !== meshId));
  }, []);

  const loadObjFile = useCallback(async (file) => {
    if (!file || !sceneRef.current?.scene) return;
    setObjError('');
    try {
      const hadHoles = holes.length > 0;
      const text = await file.text();
      const group = new OBJLoader().parse(text);
      // Georeference into the local frame when the mesh's coordinates land
      // inside the project's grid extent — that's how we tell a CRS-projected
      // export apart from an OBJ that is already in local meters (which we
      // must leave untouched, or the transform would fling it ~6,000 km away).
      const georeferenced = applyGeoreferenceIfInRange(group, utmToLocal);
      const id = `${file.name}:${file.lastModified || Date.now()}:${Math.random().toString(36).slice(2)}`;
      const stats = prepareObjMeshGroup(group, file.name);
      if (!stats.meshCount) throw new Error('OBJ file did not contain any mesh geometry.');
      sceneRef.current.scene.add(group);
      objMeshGroupsRef.current.set(id, group);
      reconcileSceneBounds(sceneRef.current, holes, objMeshGroupsRef.current);
      // Keep existing drillholes in view when adding a mesh. A large or
      // spatially separate OBJ can make the combined bounds enormous, and
      // refitting here makes the holes appear to disappear. When the OBJ is
      // the first content, frame it as before; users can always use the
      // explicit Fit to scene control after adding more content.
      if (!hadHoles) sceneRef.current.focusOnLastBounds?.(1.2);
      // The mesh is now framed content: without this, adding the first hole
      // afterwards would count as "first content" and refit the camera to
      // hole-only bounds, throwing the loaded mesh out of view.
      hasFramedContentRef.current = true;
      setObjMeshes((prev) => [...prev, { id, name: file.name, georeferenced, ...stats }]);
    } catch (e) {
      setObjError(e?.message || String(e));
    } finally {
      if (objFileInputRef.current) objFileInputRef.current.value = '';
    }
  }, [holes, utmToLocal]);

  // Initialise scene once.
  useEffect(() => {
    if (!containerRef.current) return;
    const scene = new Baselode3DScene();
    let viewSaveInterval = null;
    scene.init(containerRef.current);
    scene.setDrillholeClickHandler((meta) => setSelectedHole(meta));
    scene.setControlMode(controlMode);
    if (typeof scene.setViewChangeHandler === 'function') {
      scene.setViewChangeHandler((viewState) => saveCachedCameraView(viewState));
    } else {
      viewSaveInterval = window.setInterval(() => {
        const viewState = getSceneViewState(scene);
        if (viewState) saveCachedCameraView(viewState);
      }, 300);
    }
    const cachedView = loadCachedCameraView();
    if (cachedView) restoredCameraRef.current = setSceneViewState(scene, cachedView);
    enforceZUpOrbit(scene);
    sceneRef.current = scene;

    const handleResize = () => scene.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      if (viewSaveInterval) window.clearInterval(viewSaveInterval);
      const viewState = getSceneViewState(scene);
      if (viewState) saveCachedCameraView(viewState);
      window.removeEventListener('resize', handleResize);
      objMeshGroupsRef.current.forEach((group) => disposeObject3D(group));
      objMeshGroupsRef.current.clear();
      scene.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    sceneRef.current?.setControlMode(controlMode);
    if (controlMode === 'orbit') enforceZUpOrbit(sceneRef.current);
  }, [controlMode]);
  useEffect(() => { sceneRef.current?.setBackground(sceneBackground); }, [sceneBackground]);
  useEffect(() => { sceneRef.current?.setCameraFov(FOV_STEPS[perspectiveLevel]); }, [perspectiveLevel]);

  // Structural discs.
  useEffect(() => {
    if (sceneRef.current && holes?.length && structureRows?.length) {
      sceneRef.current.setStructuralDiscs(structureRows, holes, { radius: 5, opacity: 0.75 });
    }
  }, [holes, structureRows]);
  useEffect(() => { sceneRef.current?.setStructuralDiscsVisible(showStructuralDiscs); }, [showStructuralDiscs]);

  // Strip logs.
  useEffect(() => {
    if (!sceneRef.current || !holes?.length) return;
    if (stripLogData?.length) sceneRef.current.setStripLogs(holes, stripLogData);
    else sceneRef.current.clearStripLogs?.();
  }, [holes, stripLogData]);

  // Drillholes + colouring.
  useEffect(() => {
    if (sceneRef.current) {
      // The local frame is centred on the collar centroid, which can sit a long
      // way (kilometres) from whichever holes the user actually adds. So the
      // first time content appears, always frame it — otherwise the orbit pivot
      // (controls.target) stays near the world origin and rotation swings the
      // content around in a wide arc. After that initial fit, honour the cached
      // / preserved view so colour-by toggles don't yank the camera.
      const firstContent = holes.length > 0 && !hasFramedContentRef.current;
      const preserveView = !firstContent
        && (renderedHolesRef.current === holes || restoredCameraRef.current);
      sceneRef.current.setDrillholes(holes, {
        selectedAssayVariable: colorByVariable === 'None' ? '' : colorByVariable,
        assayIntervalsByHole: isCategorical ? geologyCategoryIntervalsByHole : selectedAssayIntervalsByHole,
        preserveView,
        isCategoricalVariable: isCategorical,
        categoryColorMap: categoricalColorMap,
      });
      if (firstContent) enforceZUpOrbit(sceneRef.current);
      if (holes.length > 0) hasFramedContentRef.current = true;
      renderedHolesRef.current = holes;
      restoredCameraRef.current = false;
      // setDrillholes overwrites scene.lastBounds with hole-only bounds, so
      // fold any loaded OBJ meshes back in immediately — the effect below
      // doesn't re-run for colour/interval changes.
      reconcileSceneBounds(sceneRef.current, holes, objMeshGroupsRef.current);
    }
  }, [holes, colorByVariable, selectedAssayIntervalsByHole, isCategorical, geologyCategoryIntervalsByHole, categoricalColorMap]);

  useEffect(() => {
    if (!sceneRef.current) return;
    reconcileSceneBounds(sceneRef.current, holes, objMeshGroupsRef.current);
  }, [holes, objMeshes]);

  const sidebarTarget = typeof document !== 'undefined' ? document.getElementById('three-d-controls-slot') : null;
  const sidebarControls = (
    <div className="three-d-controls">
      <div className="label-caps">{t('scene.title')}</div>

      <section className="three-d-picker-section" aria-labelledby="three-d-picker-heading">
        <div className="three-d-section-heading" id="three-d-picker-heading">{t('scene.selectHole')}</div>
        <div className="three-d-section-help">{t('scene.chooseHole')}</div>

        {/* Holes with a real desurveyed drill string (>= 2 points). */}
        <label className="three-d-control">
          <span>{t('scene.holeLabel')}</span>
          <select
            value={selectedHoleId}
            onChange={(e) => setSelectedHoleId(e.target.value)}
            disabled={!holeBuckets.full.length}
          >
            <option value="">{holeBuckets.full.length ? t('scene.pickHole') : t('scene.no3dString')}</option>
            {holeBuckets.full.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="primary-button"
          onClick={() => {
            if (!selectedHoleId) return;
            addHoleToScene(selectedHoleId);
          }}
          disabled={!selectedHoleId || holes.some((h) => h.id === selectedHoleId)}
        >
          {selectedHoleId && holes.some((h) => h.id === selectedHoleId)
            ? t('scene.alreadyInScene')
            : t('scene.addToScene')}
        </button>

        {/* No drill string, but a precomputed point or collar elevation provides
            one accurate point. */}
        {holeBuckets.collar.length > 0 && (
          <>
            <label className="three-d-control">
              <span>{t('scene.holePoint')}</span>
              <select
                value={selectedCollarHoleId}
                onChange={(e) => setSelectedCollarHoleId(e.target.value)}
              >
                <option value="">Pick a hole…</option>
                {holeBuckets.collar.map((id) => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="primary-button"
              onClick={() => {
                if (!selectedCollarHoleId) return;
                if (precomputedByHole[selectedCollarHoleId]) addHoleToScene(selectedCollarHoleId);
                else addCollarPointToScene(selectedCollarHoleId);
              }}
              disabled={!selectedCollarHoleId || holes.some((h) => h.id === selectedCollarHoleId)}
            >
              {selectedCollarHoleId && holes.some((h) => h.id === selectedCollarHoleId)
                ? t('scene.alreadyInScene')
                : t('scene.addPoint')}
            </button>
          </>
        )}

        {/* No trace and no elevation — can't be placed in 3D. Listed for
            reference only; not addable. */}
        {holeBuckets.noSurvey.length > 0 && (
          <label className="three-d-control">
            <span>{t('scene.holeNoSurvey')}</span>
            <select
              value={selectedNoSurveyHoleId}
              onChange={(e) => setSelectedNoSurveyHoleId(e.target.value)}
              title={t('scene.noSurveyDesc')}
            >
              <option value="">{`${holeBuckets.noSurvey.length} hole${holeBuckets.noSurvey.length === 1 ? '' : 's'} — no 3D geometry`}</option>
              {holeBuckets.noSurvey.map((id) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
          </label>
        )}
      </section>

      {addError && <div className="error-banner small">{addError}</div>}
      {holes.length > 0 && (
        <section className="three-d-scene-section" aria-labelledby="three-d-scene-heading">
          <div className="three-d-section-heading" id="three-d-scene-heading">
            Holes in scene <span className="three-d-scene-count">{holes.length}</span>
          </div>
          <div className="three-d-section-help">Visible in the 3D view. Remove a hole with ×.</div>
          <ul className="three-d-added-list" aria-label="Holes in scene">
            {holes.map((h) => (
              <li key={h.id}>
                <span className="hole-id" title={h.id}>{h.id}</span>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeHoleFromScene(h.id)}
                  aria-label={`Remove ${h.id}`}
                  title={`Remove ${h.id}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="three-d-divider" />
      <button
        type="button"
        className="secondary-button"
        onClick={() => objFileInputRef.current?.click()}
      >
        Load OBJ mesh
      </button>
      <input
        ref={objFileInputRef}
        type="file"
        accept=".obj,text/plain"
        className="visually-hidden"
        onChange={(e) => loadObjFile(e.target.files?.[0])}
      />
      {objError && <div className="error-banner small">{objError}</div>}
      {objMeshes.length > 0 && (
        <ul className="three-d-added-list" aria-label="OBJ meshes in scene">
          {objMeshes.map((mesh) => (
            <li key={mesh.id}>
              <span
                className="hole-id"
                title={`${mesh.name} (${mesh.vertexCount} vertices) — ${mesh.georeferenced ? 'georeferenced to project collars' : 'loaded in raw mesh coordinates'}`}
              >
                {mesh.name}
              </span>
              {!mesh.georeferenced && (
                <span className="obj-mesh-flag" title="Mesh coordinates were not aligned to the project — load collars in the same CRS to overlay drillholes.">
                  raw
                </span>
              )}
              <button
                type="button"
                className="remove-btn"
                onClick={() => removeObjMeshFromScene(mesh.id)}
                aria-label={`Remove ${mesh.name}`}
                title={`Remove ${mesh.name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const dataSourceTarget = typeof document !== 'undefined' ? document.getElementById('data-source-slot') : null;
  const dataSourceInfo = (
    <div className="data-source-text">
      {(collars.length > 0 || holes?.length > 0 || objMeshes.length > 0) && (
        <div>
          {collars.length > 0 && `${collars.length} collars`}
          {holes?.length > 0 && `, ${holes.length} in scene`}
          {objMeshes.length > 0 && `, ${objMeshes.length} OBJ mesh${objMeshes.length === 1 ? '' : 'es'}`}
        </div>
      )}
    </div>
  );

  return (
    <div className="drillhole-container">
      <div className="drillhole-header">
        <h1>3D Scene</h1>
        <div className="drillhole-controls">
          <label className="drillhole-color-control">
            Color by
            <select
              className="drillhole-select"
              value={colorByVariable}
              onChange={(e) => setColorByVariable(e.target.value)}
            >
              <option value="None">None</option>
              {assayVariables.map((variable) => (
                <option key={variable} value={variable}>
                  {variable === '__HAS_ASSAY__' ? 'Has Assay Data' : variable}
                </option>
              ))}
              {geologyCategories.length > 0 && (
                <optgroup label="Geology">
                  {geologyCategories.map((v) => (<option key={v} value={v}>{v}</option>))}
                </optgroup>
              )}
            </select>
          </label>
          {structureRows?.length > 0 && (
            <label className="drillhole-color-control">
              <input type="checkbox" checked={showStructuralDiscs} onChange={(e) => setShowStructuralDiscs(e.target.checked)} />
              Structural discs
            </label>
          )}
          {!isCategorical && colorByVariable !== 'None' && colorByVariable !== '__HAS_ASSAY__' && (
            <label className="drillhole-color-control">
              <input type="checkbox" checked={showStripLogs} onChange={(e) => setShowStripLogs(e.target.checked)} />
              Strip logs
            </label>
          )}
          <label className="drillhole-color-control">
            Background
            <select
              className="drillhole-select"
              value={sceneBgKey}
              onChange={(e) => setSceneBgKey(e.target.value)}
            >
              {SCENE_BG_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label className="drillhole-projection-slider">
            Ortho
            <input
              type="range"
              min={0}
              max={FOV_STEPS.length - 1}
              step={1}
              value={perspectiveLevel}
              onChange={(e) => setPerspectiveLevel(Number(e.target.value))}
            />
            Persp
          </label>
          {holes.length > 0 && <span className="drillhole-info">{holes.length} in scene</span>}
          {error && <span className="error-text">{error}</span>}
          {colorByVariable === '__HAS_ASSAY__' && (
            <div className="drillhole-legend">
              <div className="drillhole-legend-title">Legend (Has Assay Data)</div>
              <div className="drillhole-legend-grid">
                <div className="drillhole-legend-item">
                  <span className="drillhole-legend-swatch" style={{ background: '#ff8c42' }} />
                  <span className="drillhole-legend-label">Has assay data</span>
                </div>
                <div className="drillhole-legend-item">
                  <span className="drillhole-legend-swatch" style={{ background: '#9ca3af' }} />
                  <span className="drillhole-legend-label">No assay data</span>
                </div>
              </div>
            </div>
          )}
          {isCategorical && geologyCategoryIntervalsByHole && (() => {
            const cats = [...new Set(
              Object.values(geologyCategoryIntervalsByHole)
                .flatMap((ivs) => ivs.map((iv) => iv.value))
                .filter(Boolean)
            )].sort();
            return cats.length ? (
              <div className="drillhole-legend">
                <div className="drillhole-legend-title">Legend ({colorByVariable})</div>
                <div className="drillhole-legend-grid">
                  {cats.map((cat) => (
                    <div key={cat} className="drillhole-legend-item">
                      <span className="drillhole-legend-swatch" style={{ background: colorForCategory(categoricalColorMap, cat, getCategoryHexColor(cat)) }} />
                      <span className="drillhole-legend-label">{cat}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}
          {!isCategorical && colorByVariable !== 'None' && colorByVariable !== '__HAS_ASSAY__' && legendScale && (
            <div className="drillhole-legend">
              <div className="drillhole-legend-title">Legend ({colorByVariable})</div>
              <div className="drillhole-legend-grid">
                {legendScale.bins.map((bin, index) => (
                  <div key={`${bin.index}-${index}`} className="drillhole-legend-item">
                    <span className="drillhole-legend-swatch" style={{ background: legendScale.colors[index] }} />
                    <span className="drillhole-legend-label">{bin.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="canvas-container" ref={containerRef}>
        <div className="zoom-slider-overlay">
          <span className="zoom-label">+</span>
          <div className="zoom-slider-track">
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              defaultValue={50}
              className="zoom-slider"
              onChange={(e) => {
                const newVal = Number(e.target.value);
                const delta = newVal - zoomSliderPrevRef.current;
                e.target.value = 50;
                zoomSliderPrevRef.current = 50;
                sceneRef.current?.dolly(Math.pow(0.95, delta));
              }}
            />
          </div>
          <span className="zoom-label">−</span>
        </div>
        {status !== 'ready' && (
          <div className="placeholder-message">
            <p>Open a project folder to view drillholes.</p>
          </div>
        )}
        {status === 'ready' && holes.length === 0 && objMeshes.length === 0 && (
          <div className="placeholder-message">
            <p>Add a hole from the sidebar to begin.</p>
          </div>
        )}
        <Baselode3DControls
          controlMode={controlMode}
          onToggleFly={() => setControlMode((m) => (m === 'orbit' ? 'fly' : 'orbit'))}
          onRecenter={() => recenterCameraToOriginZUp(sceneRef.current, 2000)}
          onLookDown={() => lookDownZUp(sceneRef.current, 3000)}
          onFit={() => focusOnLastBoundsZUp(sceneRef.current, 1.2)}
          darkBackground={darkBackground}
          onToggleDarkBackground={(e) => setSceneBgKey(e.target.checked ? 'black' : 'off-white')}
        />
        {selectedHole && (
          <div className="selection-popup">
            <div className="selection-header">{t('scene.drillholeSelected')}</div>
            <div className="selection-body">
              <div><strong>Hole ID:</strong> {selectedHole.holeId}</div>
              <div><strong>Project:</strong> {selectedHole.project || t('scene.na')}</div>
            </div>
            <button className="ghost-button small" type="button" onClick={() => setSelectedHole(null)}>{t('scene.close')}</button>
          </div>
        )}
      </div>
      {dataSourceTarget && createPortal(dataSourceInfo, dataSourceTarget)}
      {sidebarTarget && createPortal(sidebarControls, sidebarTarget)}
    </div>
  );
}

export default Drillhole;
